import * as React from 'react';
import { COMPONENT_TYPE_LIST, Component, FieldRef, ScreenLink } from '../jsonValidator';
import Popup from '../../../components_v2/popup/Popup';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import PopupCreation from '../../../components_v2/popup/PopupCreation';
import Input from '../../../components_v2/input/Input';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { getPageList, removeComponent, renamePageAndNextScreens } from './formProcessingTools';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import * as _ from 'lodash';
import Add from '../../../components_v2/add/Add';
import { DeleteDot } from '../../../styles/global/css/Dot';
import styled from 'styled-components';
import { FlexDiv } from '../../products/style';
import { FormEditorContext } from './visualFormEditor';
import {
	AmberSidely,
	PrimaryColor,
	BorderColor,
	PastelPrimaryColor,
	OrangeSidely,
	RedSidely,
	SurfaceColor
} from '../../../styles/global/css/Utils';
import { Translate, translateToString } from '../../../styles/global/translate';
import { AddToLayoutAndFields, EditLayout } from './layoutEditor';
import { Field, getFields } from '../../../atoms/forms';
import { Trash2 } from 'lucide-react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import DragAndDropDots from '../../../images/icons/drag_and_drop_dots.svg';
import { BoolCheckbox } from '../PopupField';
import { useRecoilValue } from 'recoil';
import { AAdditionalColumns } from '../../../atoms/additionalColumns';
export const ComponentWrapper = styled.div`
	--border-color-wr: ${BorderColor};
	position: relative;
	width: 100%;
	padding: 10px;
	border: 1px solid var(--border-color-wr);
	border-radius: 5px;
	display: flex;
	gap: 8px;
	align-items: center;
	font-size: 13px;
	margin-bottom: 8px;
	& > #setting-icons {
    visibility: hidden;
    }
	&:hover {
		--border-color-wr: ${PrimaryColor};
		& > #setting-icons {
			visibility: visible;
		}
	}
`;

const HighlightField = styled.span`
	font-weight: 700;
	border-radius: 7px;
	padding: 3px;
`;

const ComponentTitle = styled.div`
	font-weight: bold;
	display: inline;
`;

export const Wrapper = styled.div<{ warningType?: string }>`
	width: 100%;
	overflow: auto;
	padding: 15px;
	${p => p.warningType ? `border: 2px solid ${p.warningType === 'error' ? RedSidely : `color-mix(in oklab, ${OrangeSidely} 50%, transparent)`}; ` : ''}
	border-radius: 5px;
`;

export const GroupWrapper = styled.div`
	margin: 10px;
	border: 1px solid color-mix(in oklab, ${AmberSidely} 20%, transparent);
	border-radius: 5px;
`;


export const WarningWrapper = styled.div`
	padding: 15px;
	border: 2px solid color-mix(in oklab, ${OrangeSidely} 50%, transparent);
	border-radius: 5px;
`;

export const ErrorWrapper = styled.div`
	padding: 15px;
	border: 2px solid ${RedSidely};
	border-radius: 5px;
`;

const Arrow = styled.span`
&:before {
	content: '-> ';
}
`;

const WarningMessage = styled(FlexDiv)`
	color: yellow;
`;

export const RemoveLink = styled.span`
	color: ${PrimaryColor};
	text-decoration: underline;
	cursor: pointer;
`;

const ModeSwitch = styled.div<{ active?: boolean }>`
	background-color: ${p => p.active ? PrimaryColor : SurfaceColor};
	padding: 3px;
	font-size: 13px;
	color: ${p => p.active ? 'white' : PrimaryColor};
	border-radius: 5px;
	&:hover {
		cursor: pointer;
		background-color: ${p => p.active ? PrimaryColor : PastelPrimaryColor};
	}
	transition: .1s;
`;

const EditPaginationCard = styled.div`
	--border-color-wr: ${BorderColor};
	border: 1px solid var(--border-color-wr);
	border-radius: 5px;
	width: 100%;
	position: relative;
	&:hover {
		& > #setting-icons {
			visibility: visible;
		}
	}
`;

const SettingsWrapper = styled.div`
	display: flex;
	align-items: center;
	visibility: hidden;
	position: absolute;
	top: 5px;
	right: 5px;
	gap: 4px;
`;

export function BoolEditor(props: { path: string, pageNumber: number, defaultIsEnabled?: boolean }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const value: boolean | undefined = (_.get(form[props.pageNumber], props.path) === undefined) ? props.defaultIsEnabled : _.get(form[props.pageNumber], props.path);

	return <BoolCheckbox
		type='checkbox'
		checked={value}
		onClick={() => {
			const newForm = _.cloneDeep(form);
			_.set(newForm[props.pageNumber], props.path, !value);
			setForm(newForm);
		}}
	/>;
}

function SwitchMode(props: { path: string, pageNumber: number, value: any, nullIsUndefined?: boolean, labelForNull?: string }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const [mode, setMode] = React.useState<any>(typeof props.value === 'boolean' ? props.value.toString() : (props.value === null) ? 'null' : 'null');

	React.useEffect(() => {
		const newForm = _.cloneDeep(form);
		_.set(newForm[props.pageNumber], props.path, mode === 'true' ? true : mode === 'false' ? false : (props.nullIsUndefined ? undefined : null));
		setForm(newForm);
	}, [mode]);


	return <>
		<FlexDiv>
			<ModeSwitch onClick={() => setMode('true')} active={mode === 'true'}><Translate id='yes' /></ModeSwitch>
			<ModeSwitch onClick={() => setMode('false')} active={mode === 'false'}><Translate id='no' /></ModeSwitch>
			<ModeSwitch onClick={() => setMode('null')} active={mode === 'null'}>{props.labelForNull ?? translateToString('form_editor.undetermined')}</ModeSwitch>
		</FlexDiv>
	</>;
}

export function MetaDataEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const metadata = _.get(form[props.pageNumber], props.path);
	return <>
		<div>
			<Translate id='form_editor.metadata_for_field' /> <br />
			{metadata && Object.entries(metadata).map(([key, value]) => {
				return <>
					<FlexDiv>
						<DeleteDot style={{ marginRight: '8px' }} onClick={
							() => {
								const newForm = _.cloneDeep(form);
								_.set(newForm[props.pageNumber], props.path + '.' + key, undefined);
								setForm(newForm);
							}} />
						<span style={{ fontSize: '13px' }}>{key} - </span>
						<SwitchMode path={props.path + '.' + key} pageNumber={props.pageNumber} value={value} />
					</FlexDiv>
					<br />
				</>;
			})}
		</div>
	</>;
}

export function DefaultValueEditor(props: { path: string, pageNumber: number, field: Field }): JSX.Element {
	const { form } = React.useContext(FormEditorContext);
	let element: JSX.Element | undefined = undefined;
	switch (props.field.type) {
		case 'Text':
		case 'Select':
		case 'Multiselect':
		case 'TextArea':
			element = <StringEditor path={props.path} pageNumber={props.pageNumber} />;
			break;
		case 'Boolean':
			element = <SwitchMode path={props.path} pageNumber={props.pageNumber} nullIsUndefined value={_.get(form[props.pageNumber], props.path)} labelForNull={translateToString('form_editor.none_option')} />;
			break;
		case 'Number':
		case 'Integer':
			element = <NumberEditor path={props.path} pageNumber={props.pageNumber} />;
			break;
	}
	if (element) {
		return <>
			<div>
				<Translate id='form_editor.default_value' />
				{element}
			</div>
		</>;
	} else {
		return <></>;
	}
}

const SidelyRadioButton = styled.input<{ marginRight?: string }>`
	margin-right: ${p => p.marginRight ? p.marginRight : '5px'};
	accent-color: hsl(202, 97%, 44%);
	filter: brightness(114%);
`;

export function VisibleIfEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm, advancedMode } = React.useContext(FormEditorContext);
	const [conditionType, setConditionType] = React.useState<'filled' | 'true' | 'false' | 'equal' | 'personalized' | 'never'>();
	const [fieldList, setFielList] = React.useState<Field[]>();
	const fields = form[props.pageNumber].fields;
	const [selectedField, setSelectedField] = React.useState<string>();
	const [equalTo, setEqualTo] = React.useState<string | undefined>();
	const [fieldListDropdown, setFieldListDropdown] = React.useState<DropdownData[]>([{ value: undefined, label: translateToString('form_editor.select_field_placeholder') }]);
	const backgroundColorRadioChecked = `color-mix(in oklab,color-mix(in oklab,${PrimaryColor} 11%,${SurfaceColor}),${SurfaceColor} 55%)`;

	React.useEffect(() => {
		getFields().then(fl => {
			setFielList(fl);
		});
	}, []);

	React.useEffect(() => {
		if (!fieldList) {
			return;
		}

		const newDropdown: DropdownData[] = [{ value: undefined, label: translateToString('form_editor.select_field_placeholder') }];
		fields.forEach(field => {
			if (field.slug) {
				newDropdown.push({ value: field.slug, label: fieldList.find(f => f.slug === field.slug)?.name ?? field.slug });
			}
		});
		setFieldListDropdown(newDropdown);
	}, [fieldList]);

	React.useEffect(() => {
		const visibleIf = _.get(form[props.pageNumber], props.path);

		const filled_pattern = /^current_screen\.fields\[".*"\]\.filled$/;
		const extract_filled_pattern = /^current_screen\.fields\["(.+)"\]\.filled$/;

		const true_pattern = /^current_screen\.fields\[".*"\]\.value == true$/;
		const extract_true_pattern = /^current_screen\.fields\["(.+)"\]\.value == true$/;

		const false_pattern = /^current_screen\.fields\[".*"\]\.value == false$/;
		const extract_false_pattern = /^current_screen\.fields\["(.+)"\]\.value == false$/;

		const equal_pattern = /^current_screen\.fields\[".*"\]\.value == ".*"$/;
		const extract_equal_pattern = /^current_screen\.fields\["(.+)"\]\.value == "(.*)"$/;

		if (visibleIf === 'false') {
			setConditionType('never');
		} else if (filled_pattern.test(visibleIf)) {
			setConditionType('filled');
			const match = visibleIf.match(extract_filled_pattern);
			if (match?.[1]) setSelectedField(match[1]);
		} else if (true_pattern.test(visibleIf)) {
			setConditionType('true');
			const match = visibleIf.match(extract_true_pattern);
			if (match?.[1]) setSelectedField(match[1]);
		} else if (false_pattern.test(visibleIf)) {
			setConditionType('false');
			const match = visibleIf.match(extract_false_pattern);
			if (match?.[1]) setSelectedField(match[1]);
		} else if (equal_pattern.test(visibleIf)) {
			setConditionType('equal');
			const match = visibleIf.match(extract_equal_pattern);
			if (match?.[1]) {
				setSelectedField(match[1]);
				setEqualTo(match[2]);
			}
		} else if (visibleIf !== undefined) {
			setConditionType('personalized');
		}
	}, []);

	return <>
		{fieldList && <FlexDiv flow='column' align='stretch' gap='8px'>
			<FlexDiv padding='10px' border={`1px solid ${conditionType !== undefined ? BorderColor : PrimaryColor}`} borderRadius='5px' cursor='pointer' backgroundColor={conditionType === undefined ? backgroundColorRadioChecked : undefined} onClick={
				() => {
					setSelectedField(undefined);
					if (conditionType !== undefined) {
						setConditionType(undefined);
						const newForm = _.cloneDeep(form);
						_.set(newForm[props.pageNumber], props.path, undefined);
						setForm(newForm);
					}
				}
			} >
				<SidelyRadioButton checked={conditionType === undefined} type='radio' />
				<Translate id='form_editor.always_visible' />
			</FlexDiv>
			<FlexDiv flow='column' align='stretch' gap='8px' padding='10px' border={`1px solid ${selectedField === undefined ? BorderColor : PrimaryColor}`} borderRadius='5px' cursor='pointer' backgroundColor={selectedField !== undefined ? backgroundColorRadioChecked : undefined}>
				<FlexDiv onClick={
					() => {
						if (selectedField === undefined && fieldListDropdown.length > 1) {
							setSelectedField(fieldListDropdown[1].value);
							const newForm = _.cloneDeep(form);
							_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + fieldListDropdown[1].value + '"].filled');
							setForm(newForm);
							setConditionType('filled');
						}
					}
				}>
					<SidelyRadioButton type='radio' checked={selectedField !== undefined} />
					<Translate id='form_editor.choose_field_for_visibility' /> :
				</FlexDiv>
				<Dropdown name='field' datalist={fieldListDropdown} selectedValue={fieldListDropdown.find(f => f.value === selectedField)}
					onChange={
						field => {
							if (field.value) {
								setSelectedField(field.value);
								setConditionType(undefined);
							} else {
								setSelectedField(undefined);
								setConditionType(undefined);
								_.set(form[props.pageNumber], props.path, undefined);
							}
						}
					} />
				{selectedField && <>
					<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 10px' onClick={
						() => {
							setConditionType('filled');
							const newForm = _.cloneDeep(form);
							_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + selectedField + '"].filled');
							setForm(newForm);

						}
					}>
						<FlexDiv gap='4px' cursor='pointer' >
							<SidelyRadioButton disabled={!selectedField} checked={conditionType === 'filled'} type='radio' />
							<Translate id='form_editor.visible_if_pre' /> :
						</FlexDiv>
						<FlexDiv gap='4px'>
							<HighlightField>{fieldList.find(f => f.slug === selectedField)?.name ?? selectedField}</HighlightField> <Translate id='form_editor.is_filled' />
						</FlexDiv>
					</FlexDiv>
				</>}

				{selectedField && ['Boolean'].includes(fieldList.find(f => f.slug === selectedField)?.type ?? '') &&
					<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 10px' cursor='pointer' onClick={
						() => {
							setConditionType('true');
							const newForm = _.cloneDeep(form);
							_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + selectedField + '"].value == true');
							setForm(newForm);
						}
					}>
						<FlexDiv gap='4px' >
							<SidelyRadioButton disabled={!selectedField} checked={conditionType === 'true'} type='radio' />
							<Translate id='form_editor.visible_if_pre' /> :
						</FlexDiv>
						<FlexDiv gap='4px'>
							<HighlightField>{fieldList.find(f => f.slug === selectedField)?.name ?? selectedField}</HighlightField> <Translate id='form_editor.is_yes' />
						</FlexDiv>
					</FlexDiv>}


				{selectedField && ['Boolean'].includes(fieldList.find(f => f.slug === selectedField)?.type ?? '') &&
					<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 10px' cursor='pointer' onClick={
						() => {
							setConditionType('false');
							const newForm = _.cloneDeep(form);
							_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + selectedField + '"].value == false');
							setForm(newForm);
						}
					}>
						<FlexDiv gap='4px'>
							<SidelyRadioButton disabled={!selectedField} checked={conditionType === 'false'} type='radio' />
							<Translate id='form_editor.visible_if_pre' /> :
						</FlexDiv>
						<FlexDiv gap='4px'>
							<HighlightField>{fieldList.find(f => f.slug === selectedField)?.name ?? selectedField}</HighlightField> <Translate id='form_editor.is_no' />
						</FlexDiv>
					</FlexDiv>}


				{selectedField && ['Text', 'Date', 'Select', 'Multiselect', 'Url', 'Email', 'Phone', 'Address', 'TextArea'].includes(fieldList.find(f => f.slug === selectedField)?.type ?? '') &&
					<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 10px' cursor='pointer' onClick={
						() => {
							setConditionType('equal');
						}
					} >
						<FlexDiv gap='4px'>
							<SidelyRadioButton disabled={!selectedField} type='radio' checked={conditionType === 'equal'} />
							<Translate id='form_editor.visible_if_pre' /> :
						</FlexDiv>
						<FlexDiv gap='4px' flexWrap='wrap'>
							<HighlightField>{fieldList.find(f => f.slug === selectedField)?.name ?? selectedField}</HighlightField>
							<Translate id='is_equal_to' />
							<Input inputStyle={{ backgroundColor: SurfaceColor, width: '100%', containerWidth: '100%' }} name='equalTo' type='text' value={equalTo} onChange={
								eq => {
									setEqualTo(eq);
									const newForm = _.cloneDeep(form);
									_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + selectedField + '"].value == "' + eq + '"');
									setForm(newForm);
								}
							} />
						</FlexDiv>
					</FlexDiv>}

				{selectedField && ['Number', 'Integer', 'Money'].includes(fieldList.find(f => f.slug === selectedField)?.type ?? '') &&
					<FlexDiv flow='column' align='stretch' gap='4px' padding='0 0 0 10px' cursor='pointer' onClick={
						() => {
							setConditionType('equal');
						}
					}>
						<FlexDiv gap='4px'>
							<SidelyRadioButton disabled={!selectedField} type='radio' checked={conditionType === 'equal'} />
							<Translate id='form_editor.visible_if_pre' /> :
						</FlexDiv>
						<FlexDiv gap='4px' flexWrap='wrap'>
							<HighlightField>{fieldList.find(f => f.slug === selectedField)?.name ?? selectedField}</HighlightField>
							<Translate id='is_equal_to' />
							<Input inputStyle={{ backgroundColor: SurfaceColor, width: '100%', containerWidth: '100%' }} name='equalTo' type='number' value={equalTo} onChange={
								eq => {
									setEqualTo(eq);
									const newForm = _.cloneDeep(form);
									_.set(newForm[props.pageNumber], props.path, 'current_screen.fields["' + selectedField + '"].value == ' + eq + '');
									setForm(newForm);
								}
							} />
						</FlexDiv>
					</FlexDiv>}
			</FlexDiv>

			{advancedMode &&
				<FlexDiv width='100%' flow='column' align='stretch' gap='8px' padding='10px' border={`1px solid ${conditionType !== 'personalized' ? BorderColor : PrimaryColor}`} borderRadius='5px' cursor='pointer' backgroundColor={conditionType === 'personalized' ? backgroundColorRadioChecked : undefined} onClick={
					() => {
						setSelectedField(undefined);
						setConditionType('personalized');
					}
				}>
					<FlexDiv>
						<SidelyRadioButton checked={conditionType === 'personalized'} type='radio' />
						<Translate id='form_editor.custom_condition' />
					</FlexDiv>
					{conditionType === 'personalized' && <StringEditor path={props.path} pageNumber={props.pageNumber} />}
				</FlexDiv>
			}
			<FlexDiv padding='10px' border={`1px solid ${conditionType !== 'never' ? BorderColor : PrimaryColor}`} borderRadius='5px' cursor='pointer' backgroundColor={conditionType === 'never' ? backgroundColorRadioChecked : undefined} onClick={
				() => {
					setSelectedField(undefined);
					setConditionType('never');
					const newForm = _.cloneDeep(form);
					_.set(newForm[props.pageNumber], props.path, 'false');
					setForm(newForm);
				}
			} >
				<SidelyRadioButton checked={conditionType === 'never'} type='radio' />
				<Translate id='form_editor.never_visible' />
			</FlexDiv>
		</FlexDiv>
		}
	</>;
}

export function ValidatorEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm, advancedMode } = React.useContext(FormEditorContext);
	const [conditionType, setConditionType] = React.useState<'mandatory' | 'personalized'>();

	React.useEffect(() => {
		const validator = _.get(form[props.pageNumber], props.path + '.validator');
		const f: FieldRef = _.get(form[props.pageNumber], props.path);

		if (validator === 'current_screen.fields["' + f.slug + '"].filled') {
			setConditionType('mandatory');
		} else if (validator) {
			setConditionType('personalized');
		}
	}, []);

	return <FlexDiv flow='column' align='stretch' gap='4px'>
		<FlexDiv cursor='pointer' onClick={
			() => {
				setConditionType(undefined);
				const newForm = _.cloneDeep(form);
				_.set(newForm[props.pageNumber], props.path + '.validator', undefined);
				setForm(newForm);

			}
		}><SidelyRadioButton checked={conditionType === undefined} type='radio' /> <Translate id='form_editor.not_required' /></FlexDiv>
		<FlexDiv cursor='pointer' onClick={
			() => {
				setConditionType('mandatory');
				const newForm = _.cloneDeep(form);
				_.set(newForm[props.pageNumber], props.path + '.validator', 'current_screen.fields["' + _.get(form[props.pageNumber], props.path + '.slug') + '"].filled');
				setForm(newForm);
			}
		}><SidelyRadioButton checked={conditionType === 'mandatory'} type='radio' /> <Translate id='form_editor.required_field' /></FlexDiv>
		{advancedMode && <FlexDiv cursor='pointer' onClick={
			() => {
				setConditionType('personalized');
			}
		}><SidelyRadioButton checked={conditionType === 'personalized'} type='radio' /> <Translate id='form_editor.custom_condition' /></FlexDiv>
		}
		{conditionType === 'personalized' && <StringEditor path={props.path + '.validator'} pageNumber={props.pageNumber} />}
	</FlexDiv>;
}

function NextScreenConditionEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const [noCondition, setNoCondition] = React.useState<boolean>(_.get(form[props.pageNumber], props.path) === 'true');

	return <FlexDiv flow='column' align='stretch' gap='8px'>
		<FlexDiv gap='8px' height='30px'>
			<BoolCheckbox type='checkbox' checked={!noCondition} onClick={
				() => {
					if (!noCondition) {
						setNoCondition(true);
						const newForm = _.cloneDeep(form);
						_.set(newForm[props.pageNumber], props.path, 'true');
						setForm(newForm);
					}
					else {
						setNoCondition(false);
					}
				}
			} />
			<Translate id='form_editor.custom_condition' />
			{!noCondition && <StringEditor pageNumber={props.pageNumber} path={props.path} />}
		</FlexDiv>
	</FlexDiv>;
}

export function PageNameEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const [name, setName] = React.useState<string | undefined>(_.get(form[props.pageNumber], props.path));

	React.useEffect(() => {
		const newForm = _.cloneDeep(form);
		setForm(renamePageAndNextScreens(newForm, _.get(form[props.pageNumber], props.path), name));
	}, [name]);
	return <>
		<Input
			inputStyle={{
				removePlaceHolderOnFocus: true,
				width: '100%',
				containerWidth: '100%',
				borderRadius: '5px'
			}}
			name='name'
			type='text'
			value={_.get(form[props.pageNumber], props.path)}
			onChange={setName}
		/>
	</>;

}

function NumberEditor(props: { path: string, pageNumber: number }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const [number, setNumber] = React.useState<number | undefined>(_.get(form[props.pageNumber], props.path));

	React.useEffect(() => {
		const newForm = _.cloneDeep(form);
		_.set(newForm[props.pageNumber], props.path, number);
		setForm(newForm);
	}, [number]);

	return <>
		<Input inputStyle={{ backgroundColor: SurfaceColor }} name='number' type='number' value={_.get(form[props.pageNumber], props.path)} onChange={setNumber} />
	</>;
}


export function StringEditor(props: { path: string, pageNumber: number, disabled?: boolean }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const [text, setText] = React.useState<string | undefined>(_.cloneDeep(_.get(form[props.pageNumber], props.path)));

	React.useEffect(() => {
		const newForm = _.cloneDeep(form);
		_.set(newForm[props.pageNumber], props.path, text);
		setForm(newForm);
	}, [text]);

	return <Input inputStyle={{ backgroundColor: SurfaceColor, display: 'inline', borderRadius: '5px', width: '100%', containerWidth: '100%' }} disabled={props.disabled} name='text' type='text' value={_.get(form[props.pageNumber], props.path)} onChange={setText} />;
}

export const AddComponent = styled.div`
	padding: 4px 8px;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 4px;
	border-radius: 5px;

	&:hover {
		cursor: pointer;
		background-color: ${PastelPrimaryColor};
		& > #trash-bin {
			visibility: visible;
		}
	}
`;

export function ComponentsLibrary(props: { setComponent: (component: Component) => void, advancedMode?: boolean }): JSX.Element {
	const [popupOpen, setPopupOpen] = React.useState(false);
	const [componentName, setComponentName] = React.useState('');
	const [componentType, setComponentType] = React.useState('');
	const [companyAcIds, setCompanyAcIds] = React.useState<number[]>([]);
	const pendingFactory = React.useRef<((name: string) => Component) | null>(null);
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const dataList = additionalColumns.map((ac) => { return { label: ac.name, value: ac.id }; });
	const openPopup = (factory: (name: string) => Component) => {
		pendingFactory.current = factory;
		setComponentName('');
		setPopupOpen(true);
	};

	const confirm = () => {
		if (componentName.trim() && pendingFactory.current) {
			let component = pendingFactory.current(componentName);
			if (componentType === 'additional_column_qualifier_2') {
				component = { ...component, data: { name: 'additionnal-columns', value: `get_additional_columns_by_ids({${companyAcIds.join()}})` } };
			}
			props.setComponent(component);
			setPopupOpen(false);
		}
	};

	return <>
		<Popup
			isOpen={popupOpen}
			popupMode={PopupMode.Centered}
			popupStyle={componentType === 'additional_column_qualifier_2' ? { width: '500px', height: '400px' } : { width: '400px', height: '250px' }}
			onClickOut={() => { setPopupOpen(false); setComponentType(''); }}
			content={
				<PopupCreation
					title={componentType === 'additional_column_qualifier_2' ? translateToString('form_editor.component_creation') : translateToString('form_editor.component_name_title')}
					onClose={() => { setPopupOpen(false); setComponentType(''); }}
					onSubmit={confirm}
					canValidate={componentName.trim().length > 0}
				>
					<form onSubmit={(e) => { e.preventDefault(); confirm(); }} style={{ height: '100%' }}>
						<FlexDiv flow='column' align='stretch' justify={componentType === 'additional_column_qualifier_2' ? 'space-around' : 'center'} gap='12px' padding='0 10px' height='100%' fontSize='13px' width='100%'>
							<FlexDiv flow='column' align='stretch' gap='12px'>
								<Translate id='form_editor.component_name_title' />
								<Input
									name='component_name'
									type='text'
									value={componentName}
									onChange={setComponentName}
									inputStyle={{ backgroundColor: SurfaceColor, width: '100%', containerWidth: '100%', borderRadius: '5px' }}
								/>
							</FlexDiv>
							{componentType === 'additional_column_qualifier_2' &&
								<FlexDiv flow='column' align='stretch' gap='12px' width='100%'>
									<Translate id='form_editor.component_list.additional_column_qualifier_2' />
									<Dropdown
										name='additionnal_columns'
										datalist={dataList}
										isMulti
										doNotDeleteOptionOnSelect
										checkbox
										dropdownStyle={{ optionWidth: '100%', width: '100%', containerWidth: '100%', optionUp: true, optionHeight: '160px' }}
										onChange={(data) => setCompanyAcIds(data.map((ac) => ac.value))}
									/>
								</FlexDiv>
							}
						</FlexDiv>
					</form>
				</PopupCreation>
			}
		/>
		<FlexDiv flow='column' align='stretch' gap='4px'>
			{props.advancedMode && <>
				<AddComponent onClick={() => openPopup(n => ({ type: 'text', name: _.kebabCase(n), data: 'texte a afficher (fonction lua)' }))}>
					<Translate id='form_editor.component_list.text' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'button', name: _.kebabCase(n), data: 'texte du bouton' }))}>
					<Translate id='form_editor.component_list.button' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'card_list', name: _.kebabCase(n), data: { columns: [] } }))}>
					<Translate id='form_editor.component_list.card_list' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'summary', name: _.kebabCase(n), data: [] }))}>
					<Translate id='form_editor.component_list.summary' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'additional_column_qualifier', name: _.kebabCase(n), data: { 'name': 'rayon', 'value': 'filter_map_additional_columns(getAdditionalColumns())' } }))}>
					<Translate id='form_editor.component_list.additional_column_qualifier' />
				</AddComponent>
				<AddComponent onClick={() => { openPopup(n => ({ type: 'additional_column_qualifier', name: _.kebabCase(n), data: { name: 'additionnal-columns', value: 'get_additional_columns_by_ids({})' } })); setComponentType('additional_column_qualifier_2'); }}>
					<Translate id='form_editor.component_list.additional_column_qualifier_2' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'image', name: _.kebabCase(n), data: 'image url' }))}>
					<Translate id='form_editor.component_list.image' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'links', name: _.kebabCase(n), data: [''], options: { display: 'text' } }))}>
					<Translate id='form_editor.component_list.links' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'table', name: _.kebabCase(n), data: { columns: [] } }))}>
					<Translate id='form_editor.component_list.table' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'pagination', name: _.kebabCase(n), data: { name: 'pagination', value: [] } }))}>
					<Translate id='form_editor.component_list.pagination' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'catalogue_dispatcher', name: _.kebabCase(n), screen: { screen_name: undefined, condition: 'true' } }))}>
					<Translate id='form_editor.component_list.catalogue_dispatcher' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'product_dispatcher', name: _.kebabCase(n), screen: { screen_name: undefined, condition: 'true' } }))}>
					<Translate id='form_editor.component_list.product_dispatcher' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'campaign_dispatcher', name: _.kebabCase(n), screen: { screen_name: undefined, condition: 'true' }, data: { columns: [] } }))}>
					<Translate id='form_editor.component_list.campaign_dispatcher' />
				</AddComponent>
				<AddComponent onClick={() => openPopup(n => ({ type: 'dispatcher', name: _.kebabCase(n), screen: { screen_name: undefined, condition: 'true' }, data: { name: 'nouvelle-liste', value: '{}' } }))}>
					<Translate id='form_editor.component_list.dispatcher' />
				</AddComponent>
			</>}
			<AddComponent onClick={() => openPopup(n => ({ type: 'linked_contact', name: _.kebabCase(n) }))}>
				<Translate id='form_editor.component_list.linked_contact' />
			</AddComponent>
			<AddComponent onClick={() => openPopup(n => ({ type: 'event_creator', name: _.kebabCase(n) }))}>
				<Translate id='form_editor.component_list.event_creator' />
			</AddComponent>
		</FlexDiv>
	</>;
}

function EditLinks(props: { path: string, pageNumber: number, component: Component }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const links = props.component.data as string[];

	const pages = getPageList(form);
	let pageNames: DropdownData[] = pages.map(page => ({ value: page, label: page }));
	pageNames = [{ value: undefined, label: '---' }, ...pageNames];
	return <FlexDiv gap='4px'>
		<Translate id={'form_editor.component_list.' + props.component.type} />
		{links && links.map((link, index) => {
			return <>
				<Dropdown name='link' datalist={pageNames} selectedValue={pageNames.find(p => p.value === link)}
					onChange={link => {
						const newForm = _.cloneDeep(form);
						_.get(newForm[props.pageNumber], props.path + '.data').splice(index, 1, link.value);
						setForm(newForm);
					}}
				/>
			</>;
		})}
	</FlexDiv>;
}

export function ComponentEditor(props: { path: string, pageNumber: number, component: Component }): JSX.Element {
	const { form, setForm, advancedMode } = React.useContext(FormEditorContext);
	const component = props.component;
	const pageNumber = props.pageNumber;
	const [fields, setFields] = React.useState<Field[]>();

	React.useEffect(() => {
		getFields().then(setFields);
	}, []);

	if (fields) {
		switch (props.component.type) {
			case 'pagination': {
				return <div style={{ width: '100%' }}>
					{component && <EditPaginationComponent pageNumber={pageNumber}
						path={props.path}
						component={component}
					/>}
				</div>;
			}
			case 'catalogue_dispatcher': {
				return <div style={{ width: '100%' }}>
					{component && <EditCatalogueDispatcher pageNumber={props.pageNumber}
						path={props.path}
						component={component}
					/>}
				</div>;
			}
			case 'product_dispatcher': {
				return <div style={{ width: '100%' }}>
					{component && <EditCatalogueDispatcher pageNumber={props.pageNumber}
						path={props.path}
						component={component}
					/>}
				</div>;
			}
			case 'dispatcher': {
				return <div style={{ width: '100%' }}>
					<EditGenericDispatcher pageNumber={props.pageNumber}
						path={props.path}
						component={component} />
				</div>;
			}
			case 'campaign_dispatcher': {
				return <div style={{ width: '100%' }}>
					{component && <EditCatalogueDispatcher pageNumber={props.pageNumber}
						path={props.path}
						component={component}
					/>}
				</div>;
			}
			case 'links': {
				return <div style={{ width: '100%' }}>
					<EditLinks pageNumber={props.pageNumber}
						path={props.path}
						component={component}
					/>
				</div>;
			}
			case 'card_list': {
				const columns = (component?.data as any)?.columns;
				return <FlexDiv flow='column' align='stretch' gap='8px' width='100%'>
					<Translate id='form_editor.card_list_display' />
					{columns && columns.map((column, secondIndex) => {
						return <>
							<div>
								<EditLayout
									path={`${props.path}.data.columns[${secondIndex}]`}
									pageNumber={props.pageNumber}
									layout={column}
									fieldList={fields}
								/>
							</div>
						</>;
					})}
					<div>
						<AddToLayoutAndFields path={`${props.path}.data.columns`} pageNumber={props.pageNumber}
							layout={columns} fieldList={fields} />
					</div>
				</FlexDiv>;
			}
			case 'summary': {
				return <>
					<Translate id='form_editor.component_list.summary' />
				</>;
			}
			case 'additional_column_qualifier': {
				return <>
					<Translate id={props.component.data?.name === 'additionnal-columns' ? 'form_editor.component_list.additional_column_qualifier_2' : 'form_editor.component_list.additional_column_qualifier'} />
				</>;
			}
			case 'table': {
				const columns = (component?.data as any)?.columns;
				const onTableDragEnd = (result: DropResult) => {
					if (!result.destination) return;
					const newForm = _.cloneDeep(form);
					const cols = _.get(newForm[pageNumber], props.path + '.data.columns');
					const [removed] = cols.splice(result.source.index, 1);
					cols.splice(result.destination.index, 0, removed);
					setForm(newForm);
				};
				return <FlexDiv flow='column' align='stretch' gap='4px' width='100%'>
					<div><Translate id='form_editor.grid_display' /></div>
					<DragDropContext onDragEnd={onTableDragEnd}>
						<Droppable droppableId={props.path + '.data.columns'}>
							{(provided) => (
								<div ref={provided.innerRef} {...provided.droppableProps}>
									<FlexDiv flow='column' align='stretch' gap='8px'>
										{columns && columns.map((column, secondIndex) => (
											<Draggable key={`${props.path}.data.columns[${secondIndex}]`} draggableId={`${props.path}.data.columns[${secondIndex}]`} index={secondIndex}>
												{(provided) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
														style={{ display: 'flex', alignItems: 'center', gap: '8px', ...provided.draggableProps.style }}
													>
														<div {...provided.dragHandleProps} style={{ cursor: 'grab', flexShrink: 0, padding: '0 2px', display: 'flex', alignItems: 'center' }}>
															<img src={DragAndDropDots} />
														</div>
														<div style={{ flex: 1, minWidth: 0 }}>
															<EditLayout
																path={`${props.path}.data.columns[${secondIndex}]`}
																pageNumber={props.pageNumber}
																layout={column}
																fieldList={fields}
															/>
														</div>
													</div>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</FlexDiv>
								</div>
							)}
						</Droppable>
					</DragDropContext>
					<div>
						<AddToLayoutAndFields path={`${props.path}.data.columns`} pageNumber={props.pageNumber}
							layout={columns} fieldList={fields} />
					</div>
				</FlexDiv>;
			}
			case 'button':
			case 'image':
			case 'text': {
				return <FlexDiv flow='column' align='stretch' gap='8px' width='100%'>
					<Translate id={'form_editor.component_list.' + component.type} />
					<StringEditor pageNumber={props.pageNumber} path={`${props.path}.data`} disabled={!advancedMode} />
				</FlexDiv>;
			}
			case 'linked_contact': {
				return <>
					<Translate id='form_editor.add_contact' />
				</>;
			}
			case 'event_creator': {
				return <>
					<Translate id='form_editor.add_event' />
				</>;
			}

			default: {
				if (COMPONENT_TYPE_LIST.includes(component.type)) {
					return <div>
						<Wrapper>
							<Arrow><Translate id='form_editor.not_valid.the_component' /> ``{component.type} (<Translate id='name' />: {component.name})`` <Translate id='form_editor.not_valid.not_supported_yet' /></Arrow>
						</Wrapper>
					</div>;
				}
				return <div>
					<ErrorWrapper>
						<Translate id='form_editor.not_valid.the_component' /> ``{component.type} (<Translate id='name' />: {component.name})`` <Translate id='form_editor.not_valid.is_not_a_valid_composent_you_should' /><RemoveLink
							onClick={() => {
								if (component.name) {
									const newForm = _.cloneDeep(form);
									setForm(removeComponent(newForm, pageNumber, component.name));
								}
							}}
						><Translate id='form_editor.not_valid.remove_it' /></RemoveLink>
					</ErrorWrapper>
				</div>;
			}
		}
	}
	else {
		return <><Translate id='form_editor.loading' /></>;
	}
}


interface PaginationData {
	name: string
	value: PaginationDataValue[]
}

interface PaginationDataValue {
	name: string
	screen: ScreenLink
}

const NextPageRadio = styled.div<{ checked: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	border: 1px solid ${p => p.checked ? PrimaryColor : BorderColor};
	${p => p.checked ? `background-color: color-mix(in oklab, ${PastelPrimaryColor}, ${SurfaceColor} 55%)` : ''};
	padding: 10px;
	border-radius: 5px;
	cursor: pointer;
`;

function EditNextScreen(props: { path: string, pageNumber: number, component: ScreenLink, index?: number, deletable?: boolean, onDelete?: () => void }): JSX.Element {
	const [nextPage, setNextPage] = React.useState<string | undefined>(props.component.screen_name);
	const [calculatedNextPage] = React.useState<string | undefined>(props.component.calculated_screen_name);
	const [calculated, setCalculated] = React.useState<boolean>(props.component.calculated_screen_name !== undefined);
	const [debouncedNextPage, setDebouncedNextPage] = React.useState<string | undefined>(props.component.screen_name);

	const { form, setForm, advancedMode } = React.useContext(FormEditorContext);

	React.useEffect(() => {
		const timeout = setTimeout(() => setDebouncedNextPage(nextPage), 50);
		return () => clearTimeout(timeout);
	}, [nextPage]);

	React.useEffect(() => {
		const newForm = _.cloneDeep(form);
		const screen_name_path = props.path + '.screen_name';

		_.set(newForm[props.pageNumber], screen_name_path, debouncedNextPage);
		setForm(newForm);
	}, [debouncedNextPage]);


	const pages = getPageList(form);
	let pageNames: DropdownData[] = pages.map(page => ({ value: page, label: page }));
	pageNames = [{ value: undefined, label: '---' }, ...pageNames];

	return <>
		{(!form.find(screen => screen.name == props.component.screen_name)) && (props.pageNumber > 0) && props.component.screen_name !== undefined && <WarningMessage><Translate id='form_editor.page_not_found' /></WarningMessage>}
		<FlexDiv flow='column' align='stretch' gap='12px' border={props.deletable ? `1px solid ${BorderColor}` : undefined} padding={props.deletable ? '10px' : undefined} borderRadius={props.deletable ? '5px' : undefined}>
			<FlexDiv justify='space-between'>
				{(props.index !== undefined) && (props.index > 0) && <>{translateToString('form_editor.next_button_number', [['index', String(props.index)]])}</>}
				{props.deletable && props.onDelete && <Trash2 size={16} onClick={props.onDelete} />}
			</FlexDiv>
			<FlexDiv flow='column' align='stretch' gap='8px'>
				<NextPageRadio checked={!calculated} onClick={() => {
					if (!calculated) return;
					else {
						const newForm = _.cloneDeep(form);
						_.set(newForm[props.pageNumber], props.path + '.calculated_screen_name', undefined);
						setForm(newForm);
					}
					setCalculated(!calculated);
				}
				}>
					<SidelyRadioButton type='radio' checked={!calculated} marginRight='0' />
					<Translate id='form_editor.always_same_page' />
					<Dropdown disabled={calculated} name='next-screen-name' datalist={pageNames} selectedValue={pageNames.find(p => p.value === props.component.screen_name)}
						onChange={screenName => {
							setNextPage(screenName.value);
						}}
					/>
				</NextPageRadio>
				{advancedMode &&
					<NextPageRadio checked={calculated} onClick={() => {
						if (calculated) return;
						else {
							const newForm = _.cloneDeep(form);
							_.set(newForm[props.pageNumber], props.path + '.calculated_screen_name', calculatedNextPage ?? '');
							setForm(newForm);
						}
						setCalculated(!calculated);
					}
					}>
						<SidelyRadioButton marginRight='0' type='radio' checked={calculated} />
						<div><Translate id='form_editor.next_page_calculated' /> :</div>
						<StringEditor pageNumber={props.pageNumber} path={props.path + '.calculated_screen_name'} disabled={!calculated} />
					</NextPageRadio>
				}
			</FlexDiv>
			{advancedMode &&
				<FlexDiv flow='column' align='stretch' gap='8px'>
					<div><Translate id='form_editor.condition' /> : </div>
					<NextScreenConditionEditor path={props.path + '.condition'} pageNumber={props.pageNumber} />
				</FlexDiv>
			}
		</FlexDiv>
	</>;
}
export function EditNextScreens(props: { path: string, pageNumber: number, component: ScreenLink[] }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);

	return <FlexDiv flow='column' align='stretch' gap='8px'>
		{props.component.filter(c => c !== undefined).map((nextScreen, index) => {
			return <React.Fragment key={`${props.path}[${index}]`}>
				<EditNextScreen
					path={`${props.path}[${index}]`}
					pageNumber={props.pageNumber}
					component={nextScreen}
					index={index}
					deletable={index > 0}
					onDelete={() => {
						const newForm = _.cloneDeep(form);
						const nextScreens = _.get(newForm[props.pageNumber], 'next_screens');
						if (nextScreens) {
							nextScreens.splice(index, 1);
						}
						setForm(newForm);
					}}
				/>
			</React.Fragment>;
		})
		}
		<div style={{ marginTop: '8px' }}>
			<Add size={'20px'} onClick={() => {
				const newForm = [...form];
				_.get(newForm[props.pageNumber], props.path).push({ screen_name: undefined, condition: 'true' });
				setForm(newForm);
			}} />
		</div>
	</FlexDiv>;
}

function EditGenericDispatcher(props: { path: string, pageNumber: number, component: Component }): JSX.Element {
	return <FlexDiv flow='column' align='stretch' gap='12px'>
		<Translate id={'form_editor.component_list.' + props.component.type} />
		<FlexDiv flow='column' align='stretch' gap='8px' padding='12px'>
			<Translate id='form_editor.leads_to' />
			{props.component.screen && <EditNextScreen path={`${props.path}.screen`} pageNumber={props.pageNumber} component={props.component.screen} />}
			<Translate id='form_editor.name_colon' /> {props.component.data && <StringEditor pageNumber={props.pageNumber} path={props.path + '.data.name'} />}
			<Translate id='form_editor.value_colon' /> {props.component.data && <StringEditor pageNumber={props.pageNumber} path={props.path + '.data.value'} />}
		</FlexDiv>
	</FlexDiv>;
}

function EditCatalogueDispatcher(props: { path: string, pageNumber: number, component: Component }): JSX.Element {
	return <FlexDiv flow='column' align='stretch' gap='12px'>
		<Translate id={'form_editor.component_list.' + props.component.type} />
		<FlexDiv flow='column' align='stretch' gap='8px' padding='12px'>
			<Translate id='form_editor.list_item_leads_to' />
			{props.component.screen && <EditNextScreen path={`${props.path}.screen`} pageNumber={props.pageNumber} component={props.component.screen} />}
		</FlexDiv>
	</FlexDiv>;
}

function EditPaginationComponent(props: { path: string, pageNumber: number, component: Component }): JSX.Element {
	const { form, setForm } = React.useContext(FormEditorContext);
	const paginationData = props.component.data as PaginationData;

	const onDragEnd = (result: DropResult) => {
		if (!result.destination) return;
		const newForm = _.cloneDeep(form);
		const values = _.get(newForm[props.pageNumber], props.path + '.data.value');
		const [removed] = values.splice(result.source.index, 1);
		values.splice(result.destination.index, 0, removed);
		setForm(newForm);
	};

	return <>
		<FlexDiv flow='column' align='stretch' gap='8px'>
			<ComponentTitle>
				{props.component.name}
			</ComponentTitle>
			<div><Translate id='form_editor.type_colon' /> <Translate id='form_editor.component_list.pagination' /></div>
		</FlexDiv>
		<DragDropContext onDragEnd={onDragEnd}>
			<Droppable droppableId={props.path + '.data.value'}>
				{(provided) => (
					<div ref={provided.innerRef} {...provided.droppableProps}>
						<FlexDiv flow='column' align='stretch' gap='12px' width='100%' padding='8px 0'>
							{paginationData && paginationData.value.map((value, index) => (
								<Draggable key={`${props.path}.data.value[${index}].name`} draggableId={`${props.path}.data.value[${index}]`} index={index}>
									{(provided) => (
										<div
											ref={provided.innerRef}
											{...provided.draggableProps}
											{...provided.dragHandleProps}
											style={provided.draggableProps.style}
										>
											<EditPaginationCard>
												<FlexDiv flow='column' align='stretch' padding='12px' gap='8px' width='100%'>
													<FlexDiv gap='4px'>
														<div><Translate id='name' /> :</div>
														<StringEditor pageNumber={props.pageNumber} path={`${props.path}.data.value[${index}].name`} />
													</FlexDiv>
													<Translate id='form_editor.leads_to' />
													<EditNextScreen path={`${props.path}.data.value[${index}].screen`} pageNumber={props.pageNumber} component={value.screen} />
												</FlexDiv>
												<SettingsWrapper id='setting-icons'>
													{paginationData.value.length > 1 && <div style={{ cursor: 'pointer' }}><Trash2 size={16} onClick={() => {
														const newForm = _.cloneDeep(form);
														_.get(newForm[props.pageNumber], props.path + '.data.value').splice(index, 1);
														setForm(newForm);
													}} /></div>}
												</SettingsWrapper>
											</EditPaginationCard>
										</div>
									)}
								</Draggable>
							))}
							{provided.placeholder}
						</FlexDiv>
					</div>
				)}
			</Droppable>
		</DragDropContext>
		<Add size='20px' onClick={() => {
			const newForm = _.cloneDeep(form);
			_.get(newForm[props.pageNumber], props.path + '.data.value').push({ name: 'none', screen: { screen_name: undefined, condition: 'true' } });
			setForm(newForm);
		}} />
	</>;

}
